import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '../../components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { FileSignature, Plus, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { useLanguage } from '../../contexts/LanguageContext';

export interface Agreement {
  id: number;
  carBrand: string;
  carModel: string;
  carYear: number;
  carColor?: string | null;
  carChassisNo?: string | null;
  carPlateNo?: string | null;
  sellerName: string;
  sellerIdNo: string;
  sellerAddress?: string | null;
  sellerContact: string;
  buyerName: string;
  buyerIdNo: string;
  buyerAddress?: string | null;
  buyerContact: string;
  agreedPrice?: number | null;
  currency: 'IQD' | 'USD';
  feeFromSeller?: number | null;
  feeFromBuyer?: number | null;
  totalFee?: number | null;
  agreementDate: string;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

function formatDate(s: string) {
  const d = new Date(s);
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
}

export default function AgreementsIndex() {
  const { t } = useLanguage();
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
      const data = await api.get(`/api/agreements?${params.toString()}`);
      const list = (data as { agreements?: Agreement[] })?.agreements ?? data;
      setAgreements(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  if (loading && agreements.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('agreement.title')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('agreement.subtitle')}</p>
          </div>
          <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg">
            <Link to="/agreements/add">
              <Plus className="h-4 w-4 mr-2" />
              {t('agreement.add')}
            </Link>
          </Button>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-900">
              <FileSignature className="h-5 w-5" />
              {t('agreement.list')}
            </CardTitle>
            <CardDescription className="text-slate-500">
              {t('agreement.count', { count: agreements.length })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">{t('common.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder={t('agreement.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 border-slate-200"
                />
              </div>
            </div>

            {agreements.length === 0 ? (
              <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-12 text-center">
                <p className="text-slate-500 mb-4">{t('agreement.noAgreementsYet')}</p>
                <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white">
                  <Link to="/agreements/add">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('agreement.add')}
                  </Link>
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-slate-600 font-medium text-sm">#</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('agreement.carHeader')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('agreement.sellerHeader')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('agreement.buyerHeader')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('agreement.agreedPriceHeader')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('agreement.totalFeeHeader')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('agreement.dateHeader')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agreements.map((a) => (
                    <TableRow key={a.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-sm text-slate-700">#{a.id}</TableCell>
                      <TableCell className="text-sm text-slate-700">
                        <span dir="ltr">{a.carBrand} {a.carModel} {a.carYear}</span>
                        {a.carPlateNo && <span className="text-slate-500 ml-1">({a.carPlateNo})</span>}
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">{a.sellerName}</TableCell>
                      <TableCell className="text-sm text-slate-700">{a.buyerName}</TableCell>
                      <TableCell className="text-sm text-slate-700 tabular-nums">
                        {a.agreedPrice != null ? formatPrice(a.agreedPrice, a.currency ?? 'USD') : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-emerald-600 font-medium tabular-nums">
                        {a.totalFee != null ? formatPrice(a.totalFee, a.currency ?? 'USD') : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(a.agreementDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
